import { useState, useMemo, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toastr } from 'react-redux-toastr';
import { CurrencyInput } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { MIN_ROW_HEIGHT } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import useIntl from '../../../hooks/useIntl';
import messages from './messages';
import {
  DEFAULT_TRACS_VOUCHER_ID,
  TRACS_VOUCHER_ID_PATTERN,
} from '../../../constants/hudVoucher';

export const DECIMAL_PATTERN = /^-?\d+(\.\d{1,2})?$/;
const CURRENCY_NUMBER_FORMAT_OPTIONS = { minimumFractionDigits: 2 };

export const safeNum = (v) => (v == null || isNaN(Number(v)) ? 0 : Number(v));

// Returns the effective approved amount for a bucket:
// - If `approvedAmount` has been explicitly saved, that value is used.
// - Otherwise falls back to `requestedAmount` so the first save seeds approval
//   from the requested amount rather than leaving it as zero/null.
const resolveApprovedValue = (bucketStats) => {
  const approvedAmount = bucketStats?.approvedAmount;
  const parsedApprovedAmount = Number(approvedAmount);

  if (approvedAmount == null || !Number.isFinite(parsedApprovedAmount)) {
    return safeNum(bucketStats?.requestedAmount);
  }

  return parsedApprovedAmount;
};

const toFormNumberString = (value) => {
  if (value == null || value === '') return '';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(parsed) : '';
};

const buildDefaultValues = (tracsVoucherId, voucherStats) => ({
  tracsVoucherId:
    tracsVoucherId && tracsVoucherId !== DEFAULT_TRACS_VOUCHER_ID
      ? tracsVoucherId
      : '',
  regularCaHud: toFormNumberString(
    voucherStats?.regularAssistance?.caHudApprovedAmount,
  ),
  adjustmentsCaHud: toFormNumberString(
    voucherStats?.adjustments?.caHudApprovedAmount,
  ),
  specialClaimsCaHud: toFormNumberString(
    voucherStats?.specialClaims?.caHudApprovedAmount,
  ),
  miscAccountingCaHud: toFormNumberString(
    voucherStats?.miscellaneousAccounting?.caHudApprovedAmount,
  ),
  repaymentAgreementsCaHud: toFormNumberString(
    voucherStats?.repaymentAgreements?.caHudApprovedAmount,
  ),
});

export const useApprovePaymentModal = ({
  voucher,
  voucherStats,
  onSaveApproval,
  onFinalizeApproval,
  isSaving,
  isFinalizing,
}) => {
  const [open, setOpen] = useState(false);
  const intl = useIntl();

  const defaultValues = useMemo(
    () => buildDefaultValues(voucher?.tracsVoucherId, voucherStats),
    [voucher?.tracsVoucherId, voucherStats],
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setError,
    formState: { isValid },
  } = useForm({ defaultValues, mode: 'onChange' });

  const voucherIdRules = useMemo(
    () => ({
      required: true,
      validate: (v) => {
        const voucherId = v.trim();
        if (voucherId === DEFAULT_TRACS_VOUCHER_ID)
          return intl.formatMessage(messages.voucherIdInvalid);
        return (
          TRACS_VOUCHER_ID_PATTERN.test(voucherId) ||
          intl.formatMessage(messages.voucherIdFormat)
        );
      },
    }),
    [intl],
  );

  const watchedValues = watch();

  const rows = useMemo(() => {
    const stats = voucherStats ?? {};
    return [
      {
        key: 'regular',
        label: intl.formatMessage(messages.regularTenantAssistance),
        units: safeNum(stats.regularAssistance?.billingUnitCount),
        requested: safeNum(stats.regularAssistance?.requestedAmount),
        approved: resolveApprovedValue(stats.regularAssistance),
        caHudField: 'regularCaHud',
      },
      {
        key: 'adjustments',
        label: intl.formatMessage(messages.adjustments),
        units: safeNum(stats.adjustments?.unitCount),
        requested: safeNum(stats.adjustments?.requestedAmount),
        approved: resolveApprovedValue(stats.adjustments),
        caHudField: 'adjustmentsCaHud',
      },
      {
        key: 'specialClaims',
        label: intl.formatMessage(messages.specialClaims),
        units: safeNum(stats.specialClaims?.unitCount),
        requested: safeNum(stats.specialClaims?.requestedAmount),
        approved: resolveApprovedValue(stats.specialClaims),
        caHudField: 'specialClaimsCaHud',
      },
      {
        key: 'miscAccounting',
        label: intl.formatMessage(messages.miscAccounting),
        units: null,
        requested: safeNum(stats.miscellaneousAccounting?.requestedAmount),
        approved: resolveApprovedValue(stats.miscellaneousAccounting),
        caHudField: 'miscAccountingCaHud',
      },
      {
        key: 'repaymentAgreements',
        label: intl.formatMessage(messages.repaymentAgreements),
        units: null,
        requested: safeNum(stats.repaymentAgreements?.requestedAmount),
        approved: resolveApprovedValue(stats.repaymentAgreements),
        caHudField: 'repaymentAgreementsCaHud',
      },
    ];
  }, [intl, voucherStats]);

  const {
    caHudValues,
    totalRequested,
    totalApproved,
    totalUnits,
    totalCaHud,
    totalVariance,
    hasAnyVariance,
  } = useMemo(() => {
    const caHudValues = rows.map((r) => safeNum(watchedValues[r.caHudField]));

    const { totalRequested, totalApproved, totalUnits, totalCaHud } =
      rows.reduce(
        (acc, r, idx) => ({
          totalRequested: acc.totalRequested + r.requested,
          totalApproved: acc.totalApproved + r.approved,
          totalUnits: acc.totalUnits + (r.units ?? 0),
          totalCaHud: acc.totalCaHud + caHudValues[idx],
        }),
        { totalRequested: 0, totalApproved: 0, totalUnits: 0, totalCaHud: 0 },
      );

    const toCents = (value) => Math.round(value * 100);
    const hasAnyVariance = rows.some((r, idx) => {
      return toCents(r.approved) !== toCents(caHudValues[idx]);
    });

    return {
      caHudValues,
      totalRequested,
      totalApproved,
      totalUnits,
      totalCaHud,
      totalVariance: totalApproved - totalCaHud,
      hasAnyVariance,
    };
  }, [rows, watchedValues]);

  const headers = useMemo(
    () => [
      {
        id: 'typeOfAssistance',
        label: intl.formatMessage(messages.typeOfAssistance),
        defaultWidth: 200,
        sortable: false,
      },
      {
        id: 'unitsInBilling',
        label: intl.formatMessage(messages.unitsInBilling),
        defaultWidth: 140,
        sortable: false,
        variant: 'number',
      },
      {
        id: 'requestedAmount',
        label: intl.formatMessage(messages.requestedAmount),
        defaultWidth: 140,
        sortable: false,
        variant: 'currency',
      },
      {
        id: 'amountApproved',
        label: intl.formatMessage(messages.amountApproved),
        defaultWidth: 130,
        sortable: false,
        variant: 'currency',
      },
      {
        id: 'caHudApprovedAmount',
        label: intl.formatMessage(messages.caHudApprovedAmount),
        defaultWidth: 180,
        sortable: false,
      },
      {
        id: 'variance',
        label: intl.formatMessage(messages.variance),
        defaultWidth: 100,
        sortable: false,
        variant: 'currency',
      },
    ],
    [intl],
  );

  const footerRow = useMemo(
    () => ({
      typeOfAssistance: { value: intl.formatMessage(messages.total) },
      unitsInBilling: { variant: 'number', value: totalUnits },
      requestedAmount: {
        variant: 'currency',
        value: totalRequested,
        numberFormatOptions: CURRENCY_NUMBER_FORMAT_OPTIONS,
      },
      amountApproved: {
        variant: 'currency',
        value: totalApproved,
        numberFormatOptions: CURRENCY_NUMBER_FORMAT_OPTIONS,
      },
      caHudApprovedAmount: {
        variant: 'currency',
        value: totalCaHud,
        numberFormatOptions: CURRENCY_NUMBER_FORMAT_OPTIONS,
      },
      variance: {
        variant: 'currency',
        value: totalVariance,
        numberFormatOptions: CURRENCY_NUMBER_FORMAT_OPTIONS,
      },
    }),
    [
      intl,
      totalUnits,
      totalRequested,
      totalApproved,
      totalCaHud,
      totalVariance,
    ],
  );

  const tableRows = useMemo(
    () =>
      rows.map((row, idx) => {
        const caHudVal = caHudValues[idx];
        const amountApproved = row.approved;
        const variance = amountApproved - caHudVal;
        const hasVariance =
          Math.round(amountApproved * 100) !== Math.round(caHudVal * 100);

        return {
          id: row.key,
          typeOfAssistance: { value: row.label },
          unitsInBilling: { variant: 'number', value: row.units },
          requestedAmount: {
            variant: 'currency',
            value: row.requested,
            numberFormatOptions: CURRENCY_NUMBER_FORMAT_OPTIONS,
          },
          amountApproved: {
            variant: 'currency',
            value: amountApproved,
            numberFormatOptions: CURRENCY_NUMBER_FORMAT_OPTIONS,
          },
          caHudApprovedAmount: {
            value: (
              <Controller
                name={row.caHudField}
                control={control}
                rules={{
                  required: true,
                  validate: (v) =>
                    DECIMAL_PATTERN.test(String(v).trim()) ||
                    intl.formatMessage(messages.caHudMaxDecimals),
                }}
                render={({ field, fieldState }) => (
                  <CurrencyInput
                    {...field}
                    inputProps={{
                      step: 0.01,
                    }}
                    error={Boolean(fieldState.error)}
                    required
                    HelperTextProps={{ sx: { display: 'none' } }}
                  />
                )}
              />
            ),
          },
          variance: {
            variant: 'currency',
            value: variance,
            sx: {
              color: hasVariance ? 'error.main' : 'text.primary',
            },
            numberFormatOptions: CURRENCY_NUMBER_FORMAT_OPTIONS,
          },
        };
      }),
    [rows, caHudValues, control, intl],
  );

  // header + data rows + footer at MIN_ROW_HEIGHT each
  const tableHeight = MIN_ROW_HEIGHT * (rows.length + 2) + 32; // scrollbar height;

  const handleOpen = useCallback(() => {
    reset(defaultValues);
    setOpen(true);
  }, [defaultValues, reset]);

  const handleClose = useCallback(() => {
    if (isSaving || isFinalizing) return;
    setOpen(false);
  }, [isSaving, isFinalizing]);

  const buildPayload = useCallback(
    (data) => {
      // `row.approved` is computed by `resolveApprovedValue`: it returns the stored
      // `approvedAmount` when one has been explicitly saved, or falls back to
      // `requestedAmount` when `approvedAmount` is still null (i.e. first save).
      // This ensures the backend always receives a meaningful approvedAmount so the
      // initial approval is seeded from the requested amount automatically.
      const getRowApproved = (key) =>
        rows.find((r) => r.key === key)?.approved ?? 0;

      const getBucket = (caHudRaw, key) => ({
        caHudApprovedAmount: safeNum(caHudRaw),
        approvedAmount: getRowApproved(key),
      });

      return {
        tracsVoucherId: data.tracsVoucherId,
        buckets: {
          regular: getBucket(data.regularCaHud, 'regular'),
          adjustments: getBucket(data.adjustmentsCaHud, 'adjustments'),
          specialClaims: getBucket(data.specialClaimsCaHud, 'specialClaims'),
          miscAccounting: getBucket(data.miscAccountingCaHud, 'miscAccounting'),
          repaymentAgreements: getBucket(
            data.repaymentAgreementsCaHud,
            'repaymentAgreements',
          ),
        },
      };
    },
    [rows],
  );

  const handleApprovalFailure = useCallback(
    (error) => {
      if (error?.validation?.keys?.includes('tracsVoucherId')) {
        setError('tracsVoucherId', {
          type: 'server',
          message: intl.formatMessage(messages.voucherIdFormat),
        });
        return;
      }
      toastr.error('Error', intl.formatMessage(messages.toastrError));
    },
    [intl, setError],
  );

  const handleSaveAndClose = handleSubmit(async (data) => {
    try {
      if (onSaveApproval) {
        const payload = buildPayload(data);
        await onSaveApproval(payload);
      }
      toastr.success('Success', intl.formatMessage(messages.toastrSaveSuccess));
      setOpen(false);
    } catch (e) {
      handleApprovalFailure(e);
    }
  });

  const handleConfirmAndFinalize = handleSubmit(async (data) => {
    try {
      if (onFinalizeApproval) {
        const payload = buildPayload(data);
        await onFinalizeApproval(payload);
      }
      toastr.success(
        'Success',
        intl.formatMessage(messages.toastrFinalizeSuccess),
      );
      setOpen(false);
    } catch (e) {
      handleApprovalFailure(e);
    }
  });

  return {
    count: tableRows.length,
    totalCount: rows.length,
    open,
    handleOpen,
    handleClose,
    control,
    voucherIdRules,
    isValid,
    hasAnyVariance,
    tableRows,
    headers,
    footerRow,
    tableHeight,
    handleSaveAndClose,
    handleConfirmAndFinalize,
  };
};
